import { useEffect, useMemo, useRef, useState } from 'react';
import MilestoneGiftModal from './MilestoneGiftModal.jsx';
import CombinedMilestoneClaimModal from './CombinedMilestoneClaimModal.jsx';
import {
	hasPinAndGift,
	isClaimSnoozed,
	needsAttention,
	snoozeClaim,
} from './milestoneClaims.js';

/**
 * Shows the two-step popup for milestones with a pin and a gift, otherwise
 * the single milestone popup. Only one popup is shown per page visit.
 */
const MilestoneClaimPopups = ({ currentUser, milestone }) => {
	const { assignments, claims, isLoaded, claimPin, declinePin, claimGift } =
		milestone;
	const [combinedClaimId, setCombinedClaimId] = useState(null);
	const [isCombinedOpen, setIsCombinedOpen] = useState(false);
	const [isSingleVisible, setIsSingleVisible] = useState(false);
	const hasDecided = useRef(false);

	// Milestones with both rewards are handled by the combined popup only
	const singleAssignments = useMemo(
		() => assignments.filter((a) => !hasPinAndGift(a)),
		[assignments]
	);

	useEffect(() => {
		if (!isLoaded || hasDecided.current) return;
		hasDecided.current = true;

		const claimToShow = claims.find(
			(claim) =>
				needsAttention(claim) && !isClaimSnoozed(currentUser.id, claim.id)
		);
		if (claimToShow) {
			setCombinedClaimId(claimToShow.id);
			setIsCombinedOpen(true);
		} else if (singleAssignments.length > 0) {
			setIsSingleVisible(true);
		}
	}, [isLoaded]);

	const combinedClaim = claims.find((claim) => claim.id === combinedClaimId);

	const handleCombinedClose = () => {
		if (combinedClaim && needsAttention(combinedClaim)) {
			snoozeClaim(currentUser.id, combinedClaim.id);
		}
		setIsCombinedOpen(false);
	};

	return (
		<>
			<MilestoneGiftModal
				currentUser={currentUser}
				badgeAssignments={singleAssignments}
				visible={isSingleVisible}
				onClose={() => setIsSingleVisible(false)}
			/>
			<CombinedMilestoneClaimModal
				currentUser={currentUser}
				claim={combinedClaim}
				open={isCombinedOpen && Boolean(combinedClaim)}
				onClose={handleCombinedClose}
				onClaimPin={claimPin}
				onDeclinePin={declinePin}
				onClaimGift={claimGift}
				onOpenGift={milestone.markGiftStarted}
			/>
		</>
	);
};

export default MilestoneClaimPopups;
